/**
 * useCompliance — Fetch compliance data for a job or trip.
 * Calls the TR128 compliance API but presents data generically.
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

export interface DomainStatus {
  domain: string;
  label: string;
  score: number;      // 0–100
  status: 'pass' | 'warn' | 'fail' | 'na';
  details?: string;
  checkedAt?: string;
}

export interface ComplianceData {
  jobId: string;
  overallScore: number;
  domains: DomainStatus[];
  violations: { domain: string; field: string; message: string; severity: string; timestamp?: string }[];
  auditLog: { action: string; domain: string; actor: string; timestamp: string; detail?: string }[];
}

const DOMAIN_LABELS: Record<string, string> = {
  container_id:  'Container ID',
  pod:           'Proof of Delivery',
  eir:           'Equipment Interchange',
  cargo_info:    'Cargo Information',
  vgm:           'Verified Gross Mass',
  milestones:    'Milestones & Events',
  gps:           'GPS Tracking',
  time_slots:    'Time Slot Booking',
  documents:     'Document & Compliance',
  charges:       'Charge Transparency',
};

const EMPTY: ComplianceData = {
  jobId: '',
  overallScore: 0,
  domains: Object.entries(DOMAIN_LABELS).map(([domain, label]) => ({
    domain, label, score: 0, status: 'na' as const,
  })),
  violations: [],
  auditLog: [],
};

export function useCompliance(jobId: string | undefined) {
  const [data, setData] = useState<ComplianceData>(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const resp = await api.get<{
        score: number;
        domains: Record<string, { score: number; status: string; details?: string; checked_at?: string }>;
        violations: { domain: string; field: string; message: string; severity: string; timestamp?: string }[];
        audit_log: { action: string; domain: string; actor: string; timestamp: string; detail?: string }[];
      }>(`/tms/tr128/compliance/job/${encodeURIComponent(jobId)}`);

      const domains: DomainStatus[] = Object.entries(DOMAIN_LABELS).map(([domain, label]) => {
        const d = resp.domains?.[domain];
        return {
          domain,
          label,
          score: d?.score ?? 0,
          status: (d?.status as DomainStatus['status']) ?? 'na',
          details: d?.details,
          checkedAt: d?.checked_at,
        };
      });

      setData({
        jobId,
        overallScore: resp.score ?? 0,
        domains,
        violations: resp.violations ?? [],
        auditLog: (resp.audit_log ?? []).map((a) => ({
          action: a.action, domain: a.domain, actor: a.actor,
          timestamp: a.timestamp, detail: a.detail,
        })),
      });
    } catch {
      // API not available yet — show empty state
      setData({ ...EMPTY, jobId });
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { data, loading, refresh };
}

/** Batch compliance scores for planner */
export async function fetchBatchCompliance(jobIds: string[]): Promise<Record<string, { score: number; status: string }>> {
  if (jobIds.length === 0) return {};
  try {
    const resp = await api.post<{ results: Record<string, { score: number; status: string }> }>(
      '/tms/tr128/compliance/batch',
      { jobIds }
    );
    return resp.results ?? {};
  } catch {
    return {};
  }
}
